<template>
  <div class="card rule-property-card" :class="{ 'disabled': disabled }">
    <h5 class="card-header">{{ $t("Webportal.RuleBuilder.RuleProperties") }}</h5>
    <div class="card-body">
      <template v-if="value">
        <div class="form-group">
          <label class="col-form-label col-form-label-sm">{{ $t("Webportal.RuleBuilder.RuleType") }}</label>
          <div class="d-block">
            <b-form-radio-group buttons button-variant="outline-secondary"
                                v-model="value.type"
                                :disabled="disabled"
                                @change="itemTypeChanged">
              <b-form-radio v-for="option in getRuleTypeOptions()" :value="option.value" :key="option.value">{{ option.text }}</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="form-group">
          <label class="col-form-label col-form-label-sm">{{ $t("Webportal.RuleBuilder.RuleOperation") }}</label>
          <div class="d-block">
            <b-form-radio-group buttons button-variant="outline-secondary"
                                v-model="value.operation"
                                :disabled="disabled"
                                @change="itemChanged">
              <b-form-radio v-for="option in getRuleOperationOptions(value.type)" :value="option.value" :key="option.value">{{ option.text }}</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <template v-if="options.isBusy">
          <b-spinner small variant="primary"></b-spinner>
        </template>

        <template v-else>
          <div v-if="isWave" class="form-group">
            <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Wave') }}</label>
            <treeselect
              v-model="value.value"
              :auto-load-root-options="false"
              :options="options.waves"
              :disabled="disabled"
              :disableFuzzyMatching="true"
              :showCount="true"
              showCountOf="LEAF_DESCENDANTS"
              :defaultExpandLevel="1"
              :disable-branch-nodes="true"
              value-consists-of="LEAF_PRIORITY"
              @input="itemChanged" />
          </div>

          <div v-if="isScenario" class="form-group">
            <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Scenario') }}</label>
            <treeselect
              v-model="value.value"
              :auto-load-root-options="false"
              :options="options.scenarios"
              :disabled="disabled"
              :disableFuzzyMatching="true"
              :showCount="true"
              showCountOf="LEAF_DESCENDANTS"
              :defaultExpandLevel="1"
              :disable-branch-nodes="true"
              value-consists-of="LEAF_PRIORITY"
              @input="itemChanged" />
          </div>

          <div v-if="isProperty" class="form-group">
            <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Properties') }}</label>
            <treeselect
              v-model="value.value"
              :auto-load-root-options="false"
              :options="options.properties"
              :disabled="disabled"
              :disableFuzzyMatching="true"
              :showCount="true"
              showCountOf="LEAF_DESCENDANTS"
              :defaultExpandLevel="1"
              :disable-branch-nodes="true"
              value-consists-of="LEAF_PRIORITY"
              @input="itemChanged" />
          </div>

          <div v-if="isLocation" class="form-group">
            <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Location') }}</label>
            <custom-tree-select :placeholder="$t('Webportal.LocationFilter.Placeholder')"
              v-model="locationValue"
              :options="options.clientLocations"
              value-format="object"
              :disabled="disabled"
              :limit="4"
              :limit-text="limitText"
              @input="itemChanged" />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import CustomTreeSelect from '@/components/Shared/CustomTreeSelect';
import RuleBuilderMixin from './RuleBuilderMixin';

export default {
  name: 'RuleProperties',

  mixins: [ AppConfig, RuleBuilderMixin ],

  components: { CustomTreeSelect },

  props: {
    value: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    options: { type: Object, default: () => {} },
  },

  computed: {
    isWave() {
      return this.value.type === this.RuleType.Wave;
    },

    isScenario() {
      return this.value.type === this.RuleType.Scenario;
    },

    isProperty() {
      return this.value.type === this.RuleType.Property;
    },

    isLocation() {
      return this.value.type === this.RuleType.Location;
    },

    locationValue: {
      get() {
        return this.getRuleValueObject(this.value);
      },
      set(newValue) {
        this.value.value = newValue?.id; 
      }
    }
  },

  methods: {
    itemTypeChanged(itemType) {
      this.value.value = null;

      if (!this.getRuleOperationOptions(itemType).find(o => o.value === this.value.operation))
        this.value.operation = this.RuleOperation.Equals;

      this.itemChanged();
    },

    itemChanged() {
      // Ensure that new value is applied before emitting event (isn't ensured by b-form-radio-group)
      this.$nextTick(() => {
        if (this.options.itemChanged)
          this.options.itemChanged(this.value);
        });
    }
  }
}
</script>

<style lang="scss">
  .rule-property-card {
    height: 100%;

    &.disabled {
      opacity: 0.65;
    }
  }
</style>